import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ComponentProps, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { useUserAttributes } from "@skilitics/designer-auth/attributes";
import { logout } from "@skilitics/designer-auth/login";
import { refreshAuthState } from "@skilitics/designer-auth/state";
import {
  ContextMenu,
  Divider,
  ellipsise,
  Icon,
  MenuItem,
  SkiliticsIcon,
  useDevOptionsRevealed,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { SIDEBAR_WIDTH } from "../landing-page/sidebar/sidebarStyles";
import { PreferencesDialog } from "../preferences";

const iconSize = 14;

export const MenuIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-right: 10px;
  width: 20px;

  & + span {
    flex: 1;
    ${ellipsise};
  }
`;

type Props = {
  // The avatar/button that opens the menu.
  trigger: ReactNode;
  // When false, the user has a module open, so logout navigates back to the landing page first.
  isLandingPage?: boolean;
  // Extra items rendered between Preferences and Logout (e.g. the header's workspace section).
  additionalItems?: ReactNode;
} & Pick<ComponentProps<typeof ContextMenu>, "data-test" | "anchorOrigin" | "transformOrigin">;

export const UserAccountMenu = ({ trigger, isLandingPage, additionalItems, ...contextMenuProps }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const user = useUserAttributes();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [showPreferences, setShowPreferences] = useState(false);

  const showDevOptions = useDevOptionsRevealed();

  // If we are not on the "landing page" (that is, you have a module open),
  // then the URL is nearly certainly workspace-specific.
  // Navigate back to the landing page to avoid confusing access denied errors.
  const handleLogoutClick = async () => {
    await logout();
    if (!isLandingPage) {
      await navigate("/");
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <ContextMenu
        IconComponent={trigger}
        slotProps={{ paper: { style: { width: SIDEBAR_WIDTH } } }}
        {...contextMenuProps}
      >
        <MenuItem onClick={() => navigate("/settings")} data-test="user-menu-settings">
          <MenuIconContainer>
            <Icon icon="user-large" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("settings")}</span>
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings/billing")} data-test="user-menu-billing">
          <MenuIconContainer>
            <Icon icon="user-large" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("billing")}</span>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setShowPreferences(true)} data-test="user-menu-preferences">
          <MenuIconContainer>
            <SkiliticsIcon icon="advanced-settings" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("preferences")}</span>
        </MenuItem>
        {additionalItems}
        <Divider />
        <MenuItem onClick={handleLogoutClick} data-test="user-menu-logout">
          <MenuIconContainer>
            <Icon icon="right-from-bracket" size={iconSize} />
          </MenuIconContainer>
          <span>{formatMessage("logout")}</span>
        </MenuItem>
        {showDevOptions && (
          <MenuItem
            onClick={async () => {
              await refreshAuthState();
              // Ensure the UX notices the new state since most queries won't include all referenced
              // auth state in their keys - technically a bug but rarely important in other contexts.
              await queryClient.refetchQueries();
            }}
          >
            Refresh Authentication
          </MenuItem>
        )}
      </ContextMenu>
      {showPreferences && <PreferencesDialog open={showPreferences} onClose={() => setShowPreferences(false)} />}
    </>
  );
};

const formatStrings = {
  settings: {
    en: "My Profile",
  },
  billing: {
    en: "Billing",
  },
  preferences: {
    en: "Application Preferences",
  },
  logout: {
    en: "Logout",
  },
} satisfies FormatMessages;
